// Frames from a reference the creator hands the director: a clip or a
// picture, read in the browser, never uploaded to the library. Three frames
// at a quarter, a half and three quarters of a clip; one for a picture.
export const SAMPLE_POINTS = [0.25, 0.5, 0.75] as const;
export const GRAB_WIDTH = 960;
export const GRAB_QUALITY = 0.8;
export const MAX_FRAME_BYTES = 400_000;

/** Where in a clip of `duration` seconds the frames are taken. */
export function sampleTimes(duration: number, n = SAMPLE_POINTS.length): number[] {
  if (!Number.isFinite(duration) || duration <= 0) return [0];
  return SAMPLE_POINTS.slice(0, n).map((p) => Math.min(Math.max(0, duration - 0.05), duration * p));
}

/** A data URL's bytes, for the size cap. */
export function dataUrlBytes(dataUrl: string): number {
  const i = dataUrl.indexOf(',');
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
}

export interface Grabbed { mimeType: 'image/jpeg'; data: string }

/** Frames of a file as JPEG base64, each under the cap (the quality steps down until it fits). */
export async function grabFrames(file: File): Promise<Grabbed[]> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  const encode = (): Grabbed | null => {
    for (let q = GRAB_QUALITY; q >= 0.4; q -= 0.1) {
      const url = canvas.toDataURL('image/jpeg', q);
      if (dataUrlBytes(url) <= MAX_FRAME_BYTES) return { mimeType: 'image/jpeg', data: url.slice(url.indexOf(',') + 1) };
    }
    return null;
  };
  const objectUrl = URL.createObjectURL(file);
  try {
    if (file.type.startsWith('image/')) {
      const img = new Image();
      await new Promise<void>((ok, no) => { img.onload = () => ok(); img.onerror = () => no(new Error('the picture could not be read')); img.src = objectUrl; });
      const scale = Math.min(1, GRAB_WIDTH / img.naturalWidth);
      canvas.width = Math.round(img.naturalWidth * scale); canvas.height = Math.round(img.naturalHeight * scale);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const g = encode(); return g ? [g] : [];
    }
    const video = document.createElement('video');
    video.muted = true; video.playsInline = true; video.preload = 'auto'; video.src = objectUrl;
    await new Promise<void>((ok, no) => { video.onloadedmetadata = () => ok(); video.onerror = () => no(new Error('the clip could not be read')); });
    const scale = Math.min(1, GRAB_WIDTH / (video.videoWidth || GRAB_WIDTH));
    canvas.width = Math.round((video.videoWidth || GRAB_WIDTH) * scale); canvas.height = Math.round((video.videoHeight || 540) * scale);
    const out: Grabbed[] = [];
    for (const t of sampleTimes(video.duration)) {
      await new Promise<void>((ok) => { video.onseeked = () => ok(); video.currentTime = t; });
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const g = encode(); if (g) out.push(g);
    }
    return out;
  } finally { URL.revokeObjectURL(objectUrl); }
}
